import {
  type Cell,
  closeZoom,
  type Ctx,
  dither,
  focusInput,
  type Hotspot,
  INK,
  obox,
  pell,
  portraitCanvas,
  R,
  sprite,
  tr,
} from "../../engine";

/** Generic bedside close-up (192×108): monitor, IV pole, bed with patient, optional companion. */
export interface BedScene {
  wall: string;
  wall2: string;
  base: string;
  floor: string;
  floor2: string;
  /** Patient cast key (portrait). */
  pt: string;
  /** Portrait palette override, e.g. pale skin. */
  pale?: Record<string, string>;
  monitor(c: Ctx, now: number): void;
  iv?: boolean;
  /** Second IV bag colour (blood, oxytocin…). */
  iv2?: string;
  /** Pad under the perineum; `size` px of stain in `color`. */
  pad?: { size: number; color: string };
  mask?: boolean;
  blanket?: string;
  blanket2?: string;
  /** Pregnant belly bump. */
  belly?: boolean;
  shiver?: boolean;
  /** Clerk's hand on the fundus (massage). */
  hand?: boolean;
  /** Cast key of someone sitting at the bedside. */
  companion?: string;
  extra?(c: Ctx, now: number): void;
}

export function drawBedScene(c: Ctx, now: number, o: BedScene): void {
  R(c, o.wall, 0, 0, 192, 70);
  for (let x = 0; x < 192; x += 24) R(c, o.wall2, x, 0, 1, 70);
  R(c, o.base, 0, 68, 192, 3);
  R(c, o.floor, 0, 71, 192, 37);
  for (let x = 0; x < 192; x += 16) R(c, o.floor2, x, 71, 1, 37);
  dither(c, 0, 94, 192, 14, o.floor2, 0.45);
  R(c, "#8a90a2", 28, 33, 3, 40);
  R(c, "#6d7590", 24, 72, 11, 3);
  obox(c, "#3a3f52", 6, 3, 48, 30);
  R(c, "#05090a", 9, 6, 42, 24);
  o.monitor(c, now);
  if (o.iv) {
    R(c, "#9aa0b2", 66, 2, 2, 72);
    R(c, "#7a8092", 62, 74, 10, 2);
    obox(c, "#e6f2f8", 60, 6, 7, 12);
    R(c, "#c9dce8", 60, 14, 7, 4);
    if (o.iv2) obox(c, o.iv2, 69, 8, 6, 10);
  }
  obox(c, "#8790a8", 14, 66, 166, 4);
  R(c, "#b8bcc8", 14, 66, 166, 1);
  obox(c, "#5a6070", 20, 71, 4, 20);
  obox(c, "#5a6070", 170, 71, 4, 20);
  obox(c, "#2a2230", 18, 92, 8, 4);
  obox(c, "#2a2230", 168, 92, 8, 4);
  obox(c, "#f4f4f6", 16, 57, 162, 9);
  R(c, "#d9dcea", 16, 64, 162, 2);
  if (o.pad) {
    obox(c, "#ffffff", 112, 57, 32, 7);
    const st = o.pad.size;
    if (st) {
      R(c, o.pad.color, 128 - (st >> 1), 58, st, 4);
      dither(c, 128 - (st >> 1) - 3, 58, st + 6, 5, o.pad.color, 0.35);
    }
  }
  obox(c, "#ffffff", 16, 49, 30, 9);
  R(c, "#d9dcea", 16, 55, 30, 3);
  c.save();
  c.beginPath();
  c.rect(14, 30, 40, 22);
  c.clip();
  c.drawImage(portraitCanvas(o.pt, o.pale ?? {}), 16, 28, 32, 32);
  c.restore();
  if (o.mask) obox(c, "#cfe8f4", 26, 44, 12, 5);
  const bl = o.blanket ?? "#a9c8e8";
  const bl2 = o.blanket2 ?? "#86a8cc";
  obox(c, bl, 46, 50, 66, 9);
  R(c, bl2, 46, 56, 66, 3);
  if (o.belly) {
    pell(c, 93, 50, 18, 7, INK);
    pell(c, 93, 50, 17, 6, bl);
    R(c, "#ffffff", 86, 45, 10, 1);
  } else {
    pell(c, 90, 52, 14, 4, INK);
    pell(c, 90, 52, 13, 3, bl);
  }
  obox(c, bl, 144, 51, 30, 7);
  R(c, bl2, 144, 55, 30, 3);
  if (o.shiver) {
    const sh = Math.floor(now / 90) % 2;
    R(c, "#f4dccd", 112 + sh, 52, 2, 2);
  }
  if (o.hand) {
    obox(c, "#eec49c", 88, 40, 10, 5);
    R(c, "#d49b74", 88, 43, 10, 2);
  }
  if (o.companion) c.drawImage(sprite(o.companion).f0, 144, 12, 48, 48);
  o.extra?.(c, now);
}

export interface BedHotsCfg {
  ptLabel: string;
  monLabel?: string;
  bellyLabel?: string;
  padLabel?: string;
  companion?: string;
  companionLabel?: string;
  onMonitor: () => void;
  onBelly: () => void;
  onPad?: () => void;
  onIv?: () => void;
}

/** Hotspots matching drawBedScene's layout. Clicking a face focuses the chat box. */
export function bedHots(o: BedHotsCfg): Hotspot[] {
  const talk = (): void => {
    closeZoom();
    focusInput();
  };
  const hs: Hotspot[] = [
    { x: 6, y: 3, w: 48, h: 30, label: o.monLabel ?? tr("監視器", "Monitor"), fn: o.onMonitor },
    { x: 14, y: 30, w: 36, h: 20, label: o.ptLabel, fn: talk },
    { x: 74, y: 40, w: 40, h: 18, label: o.bellyLabel ?? tr("肚子", "Belly"), fn: o.onBelly },
  ];
  if (o.onPad)
    hs.push({ x: 110, y: 54, w: 34, h: 14, label: o.padLabel ?? tr("護墊與會陰", "Pad & perineum"), fn: o.onPad });
  if (o.onIv) hs.push({ x: 58, y: 2, w: 16, h: 34, label: tr("點滴", "IV"), fn: o.onIv });
  if (o.companion) hs.push({ x: 146, y: 14, w: 44, h: 46, label: o.companionLabel ?? "", fn: talk });
  return hs;
}

/** A bed being wheeled along tile waypoints at 3 tiles/s; null once it has arrived. */
export interface BedAnim {
  t0: number;
  pts: Cell[];
}

export function bedAnimPos(anim: BedAnim | null | undefined, now: number, def: Cell): Cell | null {
  if (!anim) return def;
  const pts = anim.pts;
  let d = ((now - anim.t0) / 1000) * 3;
  for (let i = 1; i < pts.length; i++) {
    const [ax, ay] = pts[i - 1] as Cell;
    const [bx, by] = pts[i] as Cell;
    const len = Math.abs(bx - ax) + Math.abs(by - ay);
    if (d <= len) {
      const k = d / len;
      return [ax + (bx - ax) * k, ay + (by - ay) * k];
    }
    d -= len;
  }
  return null;
}
